import { toDictionary } from '@/utils/array.js'
import { packCriteria } from '@/utils/filters'
import { request } from '@/utils/request.js'

function unpack(content) {
    const companies = toDictionary(content.companies, c => c.id)

    content.metrics.forEach(m => (m.company = companies[m.companyId]))
    content.summaries.forEach(s => (s.company = companies[s.companyId]))

    delete content.companies

    return content
}

const ordinals = ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th']

function Bps(value) {
    return value.number + ' bps'
}

function Number(value) {
    return value.number.toLocaleString()
}

function Percent(value) {
    return value.number.toFixed(1) + '%'
}

function Percentile(value) {
    const ordinal = ordinals[Math.floor(value.number) % 10]
    return value.number.toFixed(0) + ordinal + ' percentile'
}

function Currency(value) {
    if (value.number < 0) {
        return `(${Currency({ ...value, number: -value.number })})`
    }

    const options = {
        style: 'currency',
        currency: value.currency,
    }

    let number = value.number
    let abs = Math.abs(number)
    let magnitude = ''

    if (abs < 100) {
        options.maximumFractionDigits = 2
        options.minimumFractionDigits = 2
    } else {
        options.maximumSignificantDigits = 3

        if (abs > 1_000_000_000) {
            number /= 1_000_000_000
            magnitude = ' billion'
        } else if (abs > 1_000_000) {
            number /= 1_000_000
            magnitude = ' million'
        }
    }

    return number.toLocaleString([], options) + magnitude
}

const formats = {
    Bps,
    Number,
    Percent,
    Percentile,
    Currency,
}

export function formatValue(value) {
    if (value == null) {
        return null
    }

    if (typeof value === 'string') {
        return value
    }

    // return the text if there is no numeric representation
    if (value.number == null) {
        return value.text
    }

    const format = formats[value.type ?? '']

    // find the appropriate format
    if (format == null) {
        // fall back to text if no format
        return value.text?.substr(0, 100)
    }

    return format(value)
}

export async function listIncentivesForExecutive(executiveId, signal) {
    return unpack(await request.get(`/api/incentives/metrics/executives/${executiveId}`, null, { signal }))
}

function unpackAnalyses(content) {
    const companies = toDictionary(content.companies ?? [], c => c.id)
    const executives = toDictionary(content.executives ?? [], e => e.id)

    content.analyses.forEach(a => {
        a.company = companies[a.companyId]
        a.executive = executives[a.executiveId]
        a.content = convertAnalysis(a.content)
    })

    return content.analyses
}

async function searchAnalyses(criteria, pagination, sort, facets, signal = null) {
    const { ok, status, content } = await request.post(
        '/api/incentives/analyses/search',
        {
            criteria: packCriteria(criteria),
            ...pagination,
            ...sort,
            facets,
        },
        {
            signal,
        }
    )

    if (!ok) {
        throw { ok, status, content }
    }

    content.analyses = unpackAnalyses(content)

    delete content.companies
    delete content.executives

    return content
}

function snakeToCamel(str) {
    return str.toLowerCase().replace(/([_][a-z])/g, group => group[1].toUpperCase())
}

const ignored = new Set(['string', 'number'])

function snakeToCamelAll(obj) {
    if (ignored.has(typeof obj) || obj === null || obj === undefined) {
        return obj
    }

    if (Array.isArray(obj)) {
        return obj.map(v => snakeToCamelAll(v))
    }

    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [snakeToCamel(key), snakeToCamelAll(value)]))
}

function convertAnalysis(content) {
    if (
        !content.grants_and_triggers &&
        !content.ownership_outcomes &&
        !content.board_intent &&
        !content.primary_sources &&
        !content.not_available
    ) {
        return content
    }

    return snakeToCamelAll(content)
}

export async function getLatestAnalysisForExecutive(executiveId, signal) {
    const result = await searchAnalyses(
        [{ name: 'ExecutiveId', values: [executiveId] }],
        { offset: 0, limit: 1 },
        { sortField: 'createdDate', sortDirection: 'desc' },
        false,
        signal
    )

    return result.analyses[0] ?? null
}
